import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Tabs, Spin, Alert, Timeline, Button, message } from 'antd';
import { j214Api, FJ511, ApprovalEvent } from '../../services/j214Api';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import { BatchAcceptanceBridge, pwj214f1AcceptanceManifest } from '../../features/acceptance';
import { useModulePermission } from '../../hooks/usePermission';
import { useJ214Mode } from './hooks/useJ214Mode';
import { useJ214Record } from './hooks/useJ214Record';
import { useJ214Navigation } from './hooks/useJ214Navigation';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import './J214Form.css';

const J214Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewNo = searchParams.get('review');
  const [activeTab, setActiveTab] = useState('1');

  const { mode, setMode, isEditing } = useJ214Mode();
  const {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  } = useJ214Record();

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useJ214Navigation(
    useCallback(async (id: string) => { await loadRecord(id); }, [loadRecord]),
  );

  const { canCreate, canEdit, canDelete, canApprove, canPrint } = useModulePermission('pwj214f1');

  // History modal state
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<ApprovalEvent[]>([]);

  // ── Initial load (with ?review= support) ──
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        const rn = searchParams.get('review');
        if (rn) {
          const idx = ids.findIndex(r => r === rn);
          setCurrentIdx(idx >= 0 ? idx : 0);
          await loadRecord(ids[idx >= 0 ? idx : 0]);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load J214 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Approval state for SharedActionBar ──
  const approvalState: ApprovalState = {
    statusCode: (record?.fj510_80 || '').trim(),
    isApproved: (record?.fj510_80 || '').trim() === 'P',
    isPending: ['D', 'S'].includes((record?.fj510_80 || '').trim()), // 統一碼 D + 舊碼 S 過渡容忍
    isLocked: ['P', 'D', 'S'].includes((record?.fj510_80 || '').trim()),
  };

  // ── CRUD handlers ──
  const handleAddAction = useCallback(async () => {
    setCurrentIdx(-1);
    setMode('add');
    resetForAdd();
    setActiveTab('1');
  }, [setCurrentIdx, setMode, resetForAdd]);

  const handleSaveAction = useCallback(async () => {
    await handleSave(mode, async (result) => {
      const ids = await loadList();
      const newIdx = ids.indexOf(result.fj510_01);
      setCurrentIdx(newIdx >= 0 ? newIdx : 0);
      applyResult(result);
      setMode('browse');
    });
  }, [mode, handleSave, loadList, setCurrentIdx, applyResult, setMode]);

  const handleModifyAction = useCallback(() => {
    setMode('edit');
  }, [setMode]);

  const handleCancelAction = useCallback(() => {
    revertToRecord();
    setMode('browse');
    // If was adding, go back to first record
    if (mode === 'add' && allIds.length > 0) {
      setCurrentIdx(0);
      loadRecord(allIds[0]);
    }
  }, [revertToRecord, setMode, mode, allIds, setCurrentIdx, loadRecord]);

  const handleDeleteAction = useCallback(() => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除採購單 ${record?.fj510_01} 嗎？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecord(ids[0]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  // ── Approval handlers ──
  const handleSubmitClick = useCallback(() => {
    if (!record?.fj510_01) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定送審此採購單「${record.fj510_01}」？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          await j214Api.submit(record.fj510_01);
          message.success('送審成功');
          await loadRecord(record.fj510_01);
        } catch {
          message.error('送審失敗');
        }
      },
    });
  }, [record, loadRecord]);

  const handleApproveClick = useCallback(() => {
    if (!record?.fj510_01) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定核准此採購單「${record.fj510_01}」？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          await j214Api.approve(record.fj510_01);
          message.success('核准成功');
          await loadRecord(record.fj510_01);
          if (reviewNo) navigate('/erp/approvals');
        } catch {
          message.error('核准失敗');
        }
      },
    });
  }, [record, loadRecord, reviewNo, navigate]);

  const handleRejectClick = useCallback(() => {
    if (!record?.fj510_01) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定退回此採購單「${record.fj510_01}」？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await j214Api.reject(record.fj510_01);
          message.success('已退回');
          await loadRecord(record.fj510_01);
          if (reviewNo) navigate('/erp/approvals');
        } catch {
          message.error('退回失敗');
        }
      },
    });
  }, [record, loadRecord, reviewNo, navigate]);

  const handleWriteoffClick = useCallback(() => {
    if (!record?.fj510_01) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fj510_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await j214Api.writeoff(record.fj510_01);
          message.success('沖銷成功');
          await loadRecord(record.fj510_01);
        } catch {
          message.error('沖銷失敗');
        }
      },
    });
  }, [record, loadRecord]);

  const handlePrint = useCallback(() => {
    if (!record?.fj510_01) return;
    const url = j214Api.getPrintUrl(record.fj510_01);
    window.open(url, '_blank');
  }, [record]);

  const handleShowHistory = useCallback(async () => {
    if (!record?.fj510_01) return;
    setHistoryVisible(true);
    setHistoryLoading(true);
    try {
      const events = await j214Api.getHistory(record.fj510_01);
      setHistoryEvents(events);
    } catch {
      message.error('無法載入審核歷史');
    } finally {
      setHistoryLoading(false);
    }
  }, [record]);

  // ── Navigation wrappers ──
  const navFirst = useCallback(() => navTo(0, isEditing), [navTo, isEditing]);
  const navPrev = useCallback(() => navTo(currentIdx - 1, isEditing), [navTo, currentIdx, isEditing]);
  const navNext = useCallback(() => navTo(currentIdx + 1, isEditing), [navTo, currentIdx, isEditing]);
  const navLast = useCallback(() => navTo(allIds.length - 1, isEditing), [navTo, allIds.length, isEditing]);

  const handleDetailChange = useCallback((idx: number, key: keyof FJ511, value: FJ511[keyof FJ511]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, [setDetails]);

  if (loading && !record && allIds.length === 0) {
    return <div style={{ padding: 24 }}>載入中...</div>;
  }

  return (
    <div className="j214-form">
      <div className="page-wrapper">
        {/* Review banner */}
        {reviewNo && (
          <Alert
            message="您正在審核此單據"
            description={`單號: ${reviewNo}。核准或退回後將自動返回待審核清單。`}
            type="warning"
            showIcon
            banner
            style={{ marginBottom: 12 }}
          />
        )}

        <div className="page-title-bar">
          <div className="page-title-left">
            <div className="breadcrumb-inline">
              <a href="/erp">ERP</a>
              <span>/</span>
              <span className="current">研發採購單</span>
            </div>
            <span className="title-separator">|</span>
            <h1>研發採購單 (pwj214f1)</h1>
          </div>
          <div className="page-title-right">
            <span style={{ fontSize: 12, color: '#999' }}>
              {record?.fj510_01 || '(新增)'}
            </span>
          </div>
        </div>

        <div className="form-info-bar">
          <div className="field-group">
            <label data-contract-field="fj510_01">採購單號</label>
            <strong>{formData.fj510_01 || '--'}</strong>
          </div>
          <div className="field-group">
            <label data-contract-field="fj510_12">公司代號</label>
            <span>{formData.fj510_12 || '--'}</span>
          </div>
          <div className="field-group">
            <label data-contract-field="form_mode">模式</label>
            <span className={`badge ${isEditing ? 'badge-warning' : 'badge-default'}`}>
              {mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽'}
            </span>
          </div>
        </div>

        <div className="form-content">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: '1',
                label: '基本資料',
                children: (
                  <Tab1BasicInfo
                    formData={formData}
                    isEditing={isEditing}
                    getField={getField}
                    setField={setField}
                    detailCount={details.length}
                    onOpenDetails={() => setActiveTab('2')}
                  />
                ),
              },
              {
                key: '2',
                label: `採購明細 (${details.length})`,
                children: (
                  <Tab2Details
                    details={details}
                    isEditing={isEditing}
                    onAdd={addDetailRow}
                    onDelete={deleteDetailRow}
                    onDetailChange={handleDetailChange}
                  />
                ),
              },
            ]}
          />
        </div>

        {/* Spacer for fixed bottom bar */}
        <div style={{ height: 64 }} />

        {/* Approval History Modal */}
        <Modal
          title="審核歷史"
          open={historyVisible}
          onCancel={() => setHistoryVisible(false)}
          footer={<Button onClick={() => setHistoryVisible(false)}>關閉</Button>}
          width={480}
        >
          {historyLoading ? (
            <Spin />
          ) : historyEvents.length === 0 ? (
            <p style={{ color: '#999' }}>尚無審核歷史記錄</p>
          ) : (
            <Timeline
              items={historyEvents.map(ev => ({
                color: ev.action === '核准' ? 'green' : ev.action === '退回' ? 'red' : 'blue',
                children: (
                  <div>
                    <strong>{ev.action}</strong>
                    {ev.user && <span style={{ marginLeft: 8, color: '#666' }}>by {ev.user}</span>}
                    {ev.date && (
                      <span style={{ marginLeft: 8, color: '#999' }}>
                        {ev.date}
                      </span>
                    )}
                    {ev.note && <div style={{ color: '#555', marginTop: 2 }}>{ev.note}</div>}
                  </div>
                ),
              }))}
            />
          )}
        </Modal>

        {/* SharedActionBar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={navFirst}
          onNavPrev={navPrev}
          onNavNext={navNext}
          onNavLast={navLast}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onSave={handleSaveAction}
          onCancel={handleCancelAction}
          approval={approvalState}
          onSubmit={handleSubmitClick}
          onApprove={handleApproveClick}
          onReject={handleRejectClick}
          onWriteoff={handleWriteoffClick}
          onHistory={handleShowHistory}
          onPrint={handlePrint}
          onExit={() => navigate('/erp')}
          canCreate={canCreate}
          canEdit={canEdit}
          canDelete={canDelete}
          canApprove={canApprove}
          canPrint={canPrint}
          acceptanceActionIds={{
            create: 'PWJ214-ACT-CREATE',
            update: 'PWJ214-ACT-UPDATE',
            delete: 'PWJ214-ACT-DELETE',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwj214f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fj510_01 ? [record.fj510_01] : []}
          keyOf={(item) => item.fj510_01}
          queryExact={(key) => j214Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && navFirst()}
          autoFillDisabled={allIds.length === 0}
          autoFillReason="載入正式 API 已存在的第一筆研發採購單。"
          screenDraft={{ master: formData, details }}
        />
      </div>
    </div>
  );
};

export default J214Form;
